// Minimal Wishlist (vanilla JS) — active class + localStorage + header list (click-to-open)
(() => {
    const STORAGE_KEY = 'wishlist:v1';
    const TOGGLE_SEL = '[data-wishlist-toggle]';
    const LIST_SEL = '[data-wishlist-list]';
    const HEADER_WRAP_SEL = '.header__wishlist';
    const ACTIVE_CLASS = 'active';
    const OPEN_CLASS = 'is-open';

    // ---------- Storage ----------
    const load = () => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        } catch {
            return [];
        }
    };
    const save = (items) => localStorage.setItem(STORAGE_KEY, JSON.stringify(items));

    /** @type {Array<{id:string, productId?:string, title?:string, url?:string, image?:string, price?:string, ts:number}>} */
    let items = load();

    // ---------- Helpers ----------
    const getId = (el) => {
        const id =
            el?.dataset?.wishlistId ||
            el?.dataset?.productId ||
            el?.closest?.('[data-product-id]')?.dataset.productId ||
            el?.getAttribute?.('data-id') ||
            '';
        return String(id || '').trim();
    };

    const getDataFromBtn = (btn) => ({
        id: getId(btn),
        productId: btn.dataset.productId || '',
        title: btn.dataset.wishlistTitle || '',
        url: btn.dataset.wishlistUrl || '',
        image: btn.dataset.wishlistImage || '',
        price: btn.dataset.wishlistPrice || '',
        ts: Date.now(),
    });

    const has = (id) => items.some((x) => x.id === id);
    const add = (item) => {
        if (item.id && !has(item.id)) {
            items = [item, ...items];
            save(items);
        }
    };
    const remove = (id) => {
        const next = items.filter((x) => x.id !== id);
        if (next.length !== items.length) {
            items = next;
            save(items);
        }
    };

    const setBtnState = (btn, on) => {
        btn.classList.toggle(ACTIVE_CLASS, !!on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('title', on ? 'Remove from wishlist' : 'Add to wishlist');
        btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    };

    const syncButtonsById = (id) => {
        document.querySelectorAll(TOGGLE_SEL).forEach((b) => {
            if (getId(b) === id) setBtnState(b, has(id));
        });
    };

    const initButtons = (root = document) => {
        root.querySelectorAll(TOGGLE_SEL).forEach((btn) => setBtnState(btn, has(getId(btn))));
    };

    // ---------- Header list render ----------
    const listEl = () => document.querySelector(LIST_SEL);

    const escapeHtml = (s = '') =>
        s.replace(
            /[&<>"']/g,
            (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
        );

    const createItemNode = (item) => {
        const li = document.createElement('li');
        li.className = 'wishlist-item';
        li.dataset.id = item.id;
        li.innerHTML = `
      <a class="wishlist-item__image" href="${item.url || '#'}" aria-label="${escapeHtml(
            item.title,
        )}">
        <img src="${item.image || ''}" alt="${escapeHtml(item.title)}" loading="lazy">
      </a>
      <div class="wishlist-item__body">
        <a class="wishlist-item__title" href="${item.url || '#'}">${escapeHtml(
            item.title || 'Product',
        )}</a>
        ${item.price ? `<div class="wishlist-item__price">${item.price}</div>` : ''}
      </div>
      <button class="wishlist-item__remove" type="button" aria-label="Remove">×</button>
    `;
        return li;
    };

    const renderHeaderList = () => {
        const root = listEl();
        if (!root) return;
        const ul = document.createElement('ul');
        ul.className = 'wishlist-list';
        ul.setAttribute('role', 'list');
        if (items.length === 0) {
            ul.innerHTML = `<li class="wishlist-item wishlist-item--empty">Your wishlist is empty</li>`;
        } else {
            items.forEach((it) => ul.appendChild(createItemNode(it)));
        }
        root.innerHTML = '';
        root.appendChild(ul);
    };

    // ---------- Header click-to-open ----------
    const initHeaderToggle = () => {
        const header = document.querySelector(HEADER_WRAP_SEL);
        const panel = listEl();
        if (!header || !panel) return;

        // тригер — перший елемент у хедері (твій SVG) або сам хедер
        const trigger = header.firstElementChild || header;

        // доступність
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('tabindex', '0');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');

        const setOpen = (open) => {
            header.classList.toggle(OPEN_CLASS, open);
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        };

        const toggleOpen = () => setOpen(!header.classList.contains(OPEN_CLASS));

        // клік по тригеру — відкриває/закриває
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            toggleOpen();
        });

        // клавіатура
        trigger.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleOpen();
            }
            if (e.key === 'Escape') setOpen(false);
        });

        // клік поза списком — закрити
        document.addEventListener('click', (e) => {
            if (!header.classList.contains(OPEN_CLASS)) return;
            if (!header.contains(e.target)) setOpen(false);
        });

        // ESC на документі — закрити
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') setOpen(false);
        });

        // клік на посиланні або видаленні — закрити (опціонально)
        panel.addEventListener('click', (e) => {
            if (e.target.closest('.wishlist-item__remove')) return; // не закриваємо під час видалення
            if (e.target.closest('a')) setOpen(false);
        });
    };

    // ---------- Events ----------
    // Toggle from product buttons
    document.addEventListener('click', (e) => {
        const btn = e.target.closest(TOGGLE_SEL);
        if (btn) {
            e.preventDefault();
            const id = getId(btn);
            if (!id) return;
            if (has(id)) {
                remove(id);
                setBtnState(btn, false);
            } else {
                add(getDataFromBtn(btn));
                setBtnState(btn, true);
            }
            syncButtonsById(id);
            renderHeaderList();
            return;
        }

        // Remove from header list
        const rm = e.target.closest('.wishlist-item__remove');
        if (rm) {
            const item = rm.closest('.wishlist-item');
            const id = item?.dataset?.id;
            if (id) {
                remove(id);
                renderHeaderList();
                syncButtonsById(id);
            }
        }
    });

    // Cross-tab sync
    window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY) return;
        items = load();
        initButtons();
        renderHeaderList();
    });

    // Observe dynamic content
    new MutationObserver((muts) => {
        for (const m of muts) {
            m.addedNodes.forEach((n) => {
                if (!(n instanceof Element)) return;
                if (n.matches(TOGGLE_SEL) || n.querySelector?.(TOGGLE_SEL)) initButtons(n);
                if (n.matches(LIST_SEL) || n.querySelector?.(LIST_SEL)) renderHeaderList();
            });
        }
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Init
    const ready = () => {
        initButtons();
        renderHeaderList();
        initHeaderToggle();
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', ready, { once: true });
    } else {
        ready();
    }
})();
